import React, { FC, ReactNode, useContext } from 'react';
import styled from 'styled-components';

import EyeIcon from '@ad-scripts/presentation/components/lib/icons/eye_icon';
import MenuIcon from '@ad-scripts/presentation/components/common/icons/menu';
import NodeNavigation from './NodeNavigation';
import SearchInput from 'src/presentation/components/pages/ivr/components/SearchInput';
import { Content, InteractiveIconContainer } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import { NODE_LIST_ID } from 'src/presentation/components/pages/ivr/constants/ivrConstants';
import { SidePanelContext } from 'src/presentation/components/pages/ivr/IvrEditor';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';

type NodeProps = {
  'data-aid': string;
  content: ReactNode;
  description?: string | ReactNode;
  label?: string;
  searchTextState: [string, Function];
  onSearchInputChange: Function;
  onSearchInputFocus?: Function;
  readOnlyMode?: boolean;
};

const SidePanelLayout: FC<NodeProps> = ({
  'data-aid': dataAid,
  content,
  description,
  label = '',
  searchTextState,
  onSearchInputChange,
  onSearchInputFocus,
  readOnlyMode,
}) => {
  const { onNodeSelected } = useContext(SidePanelContext);
  const readOnlyModeWarning = readOnlyMode ? (
    <ReadOnlyModeContainer data-aid="read-only-warning">
      <Tooltip message={<TooltipMessage>IVR editing is Read Only.</TooltipMessage>} margin={20}>
        <ReadOnlyMessage>
          <EyeIcon /> Read Only
        </ReadOnlyMessage>
      </Tooltip>
    </ReadOnlyModeContainer>
  ) : null;

  const contentHeader = label ? (
    <Header>
      <Label data-aid={'nodeForm-title'}>{label}</Label>
      {description ? <Description data-aid={'nodeForm-description'}>{description}</Description> : null}
    </Header>
  ) : null;

  const handleIconMenuClick: React.MouseEventHandler<HTMLDivElement> = () => {
    const [, setSearchText] = searchTextState;
    onNodeSelected(NODE_LIST_ID);
    setSearchText('');
  };

  const switchToNodeList = (
    <InteractiveIconContainer data-aid="menu-icon" onClick={handleIconMenuClick}>
      <MenuIcon />
    </InteractiveIconContainer>
  );

  const nodeNavigation = <NodeNavigation />;

  const search = (
    <SearchInput
      searchTextState={searchTextState}
      onChange={onSearchInputChange}
      onFocus={onSearchInputFocus}
      autoFocus={false}
    />
  );

  const panelHeader = (
    <PanelHeader>
      {switchToNodeList}
      {nodeNavigation}
      {search}
    </PanelHeader>
  );

  return (
    <PanelWrapper data-aid={dataAid}>
      {panelHeader}
      {readOnlyModeWarning}
      {contentHeader}
      <Content>{content}</Content>
    </PanelWrapper>
  );
};

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 0 14px;
`;

const ReadOnlyModeContainer = styled.div`
  padding: 0 14px;
  margin: 0 -8px -6px; // a compensation for the icon margins
  cursor: default;
`;

const ReadOnlyMessage = styled.div`
  display: inline-flex;
  align-items: center;
  color: ${p => p.theme.colors.gray600};
  font-size: 10px;

  .iconContainer {
    height: 30px;
    width: 30px;
  }

  .eyeIcon-stroke {
    stroke: ${p => p.theme.colors.gray600};
  }
`;

const TooltipMessage = styled.p`
  text-align: center;
  margin: 0;
`;

const PanelWrapper = styled.div`
  padding: 12px 14px 0 14px;
`;

const Header = styled.div`
  padding: 12px 14px 20px 14px;
`;

const Label = styled(H3)`
  margin-bottom: 12px;
`;

const Description = styled.p`
  line-height: 1.6;
  margin: 0;
`;

export default SidePanelLayout;
